import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Upload } from "lucide-react";
import { api } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";

/**
 * Header Upload action — the SINGLE upload entry point in the app.
 *
 * Iter-123 — replaced the previous "+ Add" dropdown (which had a single
 * Upload item left after iter-122 removed Camera) with a flat, one-tap
 * Upload button. Visible on every screen size; the floating bottom bar
 * was deleted entirely so this is the only Upload affordance in the
 * project header. Clicking dispatches `onsetflow:capture` with action
 * "photos" — CaptureFab handles file picking, compression, and the
 * AssignmentDialog handoff.
 *
 * Iter-156A.5 Phase 5 — when the project is archived, this button hides
 * entirely. The archive banner explains why; offering an Upload affordance
 * the backend would 403 contradicts the calm read-only promise.
 *
 * Iter-158.13.2.20f.1 (#9) — surface-aware capture context. When the
 * user taps Upload from a Fittings / Prep surface, the header now
 * dispatches `context: { mode: "prep" }` so CaptureFab's default
 * ("On Set is the unconditional default") is overridden. Everywhere
 * else the default "onset" mode still applies. Reuses the existing
 * onsetflow:capture / CaptureFab / AssignmentDialog machinery — no
 * new capture engine.
 *
 * Naming: the file/component still exports `HeaderAddMenu` so existing
 * imports across the project keep working without a sweep. The render
 * is now a plain <button>, no DropdownMenu.
 */
export default function HeaderAddMenu() {
    const { projectId } = useParams();
    const apiBase = useApiBase();
    const location = useLocation();
    const [archived, setArchived] = useState(false);

    useEffect(() => {
        if (!projectId) {
            setArchived(false);
            return;
        }
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get(
                    `${apiBase}/${projectId}/permissions/me`,
                );
                if (!cancelled) setArchived(Boolean(data?.archived));
            } catch {
                if (!cancelled) setArchived(false);
            }
        };
        load();
        const t = setInterval(load, 60000);
        return () => {
            cancelled = true;
            clearInterval(t);
        };
    }, [projectId, apiBase]);

    if (archived) return null;

    // Iter-158.13.2.20f.1 (#9) — infer capture context from the
    // current pathname. Matches `/prep`, `/fittings`, `/prep-mode`
    // (any surface a producer would think of as "Fittings" work).
    const path = (location?.pathname || "").toLowerCase();
    const isPrepSurface =
        path.includes("/prep") ||
        path.includes("/fittings") ||
        path.includes("/prep-mode");

    const fire = () =>
        window.dispatchEvent(
            new CustomEvent("onsetflow:capture", {
                detail: isPrepSurface
                    ? { action: "photos", context: { mode: "prep" } }
                    : { action: "photos" },
            }),
        );

    return (
        <button
            type="button"
            data-testid="header-add-menu-trigger"
            data-action="upload"
            data-capture-context={isPrepSurface ? "prep" : "onset"}
            title="Upload image"
            aria-label="Upload image"
            onClick={fire}
            className="inline-flex items-center justify-center gap-1.5 rounded-md h-10 px-3 text-sm font-medium text-neutral-200 hover:text-white hover:bg-white/5"
        >
            <Upload size={16} />
        </button>
    );
}
